import React, { useRef, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { FuelTextField, FuelIcon } from '@frete.com/fuel-react';
import { formatCPF, regexOnlyNumbers } from '@utils/formatters';
import { validateCpf } from '@utils/validate';
import { getTrucker } from '@services/truckers';
import DropDown from '@components/ui/DropDown';
import AutoCompleteList from '@components/ui/AutoComplete/List';
import useDebounce from '@hooks/useDebounce';
import { TruckerInterface } from '@interface/trucker';

function FieldTrucker() {
  const { control, formState, clearErrors, setError, setValue, watch } =
    useFormContext();
  const [valueState, setValueState] = useState('');
  const truckersListRef = useRef<TruckerInterface[]>([]);
  const [showAutocomplete, setShowAutocomplete] = useState(false);
  const trucker = watch('trucker') || { id: '', name: '' };
  const maxLength = 11;
  const { debounce, suspendDebounce } = useDebounce(500);

  function cpfIsValid(cpf: string) {
    const cpfOnlyNumbers = cpf.replace(regexOnlyNumbers, '');

    if (cpfOnlyNumbers.length === maxLength && validateCpf(cpfOnlyNumbers)) {
      return true;
    }

    return false;
  }

  function setTrucker(truckerParam: { id: string; description: string }) {
    setValue('trucker.id', truckerParam.id);
    setValue('trucker.name', truckerParam.description);
  }

  function clearError() {
    clearErrors('trucker');
  }

  function clearTrucker() {
    setTrucker({ id: '', description: '' });
    truckersListRef.current = [];
  }

  function onError() {
    truckersListRef.current = [];
  }

  function onSuccess(response: TruckerInterface[]) {
    if (!response.length) {
      setError('trucker', {
        message: 'Motorista não cadastrado. Tente novamente.',
      });
      return;
    }

    truckersListRef.current = response;
    setShowAutocomplete(true);
  }

  function handleInput(e: {
    detail: {
      component: 'fuel-text-field';
      value: string;
    };
  }) {
    let { value } = e.detail;

    value = value.replace(regexOnlyNumbers, '');

    suspendDebounce();

    if (value.length > maxLength) {
      value = value.slice(0, maxLength);
    }

    if (valueState !== value) {
      clearTrucker();
      clearError();
      setShowAutocomplete(false);
    }

    if (value.length === maxLength && !cpfIsValid(value)) {
      setError('trucker', {
        message: 'Informe um CPF válido.',
      });
    }

    if (cpfIsValid(value) && valueState !== value) {
      debounce(() => getTrucker(value).then(onSuccess).catch(onError));
    }

    setValueState(formatCPF(value));
  }

  function handleSetTrucker(truckerParam: { id: string; description: string }) {
    clearError();
    setTrucker(truckerParam);
    setShowAutocomplete(false);
  }

  function handleFocus() {
    if (truckersListRef.current.length) {
      setShowAutocomplete(true);
    }
  }

  function handleBlur() {
    if (!valueState.length) {
      setError('trucker', { message: 'Campo obrigatório' });
      return;
    }

    if (!trucker.name && truckersListRef.current.length) {
      setError('trucker', {
        message: 'Para continuar, selecione o motorista',
      });
    }

    if (valueState.length && !cpfIsValid(valueState)) {
      setError('trucker', {
        message: 'Informe um CPF válido.',
      });
    }
  }

  function getMessageError(): string {
    const { errors } = formState;
    const errorTrucker = (errors.trucker?.message as string) || '';
    const errorTruckerName = (errors.trucker?.name?.message as string) || '';
    const errorTruckerId = (errors.trucker?.id?.message as string) || '';

    return errorTrucker || errorTruckerName || errorTruckerId;
  }

  return (
    <div>
      <Controller
        name="trucker.name"
        control={control}
        render={() => (
          <FuelTextField
            controlled
            label="Motorista*"
            placeholder="Digite o CPF do motorista"
            value={trucker.name || valueState}
            onFuel-input={(e) => handleInput(e)}
            onFuel-focus={() => handleFocus()}
            onFuel-blur={() => handleBlur()}
            state={getMessageError() ? 'error' : 'default'}
            hint={getMessageError()}
          />
        )}
        defaultValue=""
      />
      <DropDown
        open={showAutocomplete}
        data-testid="dropDown"
        onClose={() => setShowAutocomplete(false)}
      >
        <AutoCompleteList
          name="trucker-create-proposal"
          onSelectSuggestion={(truckerSelected) =>
            handleSetTrucker(truckerSelected)
          }
          suggestionsList={truckersListRef.current}
          emptyMessage="Motorista não cadastrado. Tente novamente."
          leftIcon={<FuelIcon name="user-area" />}
        />
      </DropDown>
    </div>
  );
}

export default FieldTrucker;
